import { useEffect, useState, useCallback, useMemo } from "react";

import { App } from "antd";

import {
  getEmployeeUnitsOptions,
  getItem as getUnit,
  getUnitsTree,
} from "@/api/units";
import { getUnitEmployees, getEmployeesOptions } from "@/api/employees";
import { getStatusOptions } from "@/api/sprintStatuses";

import { FILTER_NAMES, getLocalStorageKey } from "@/constants/sprints";

export const useFilters = ({
  unit_uuid,
  userUuid,
  initialTableParams,
  tableParams,
  setParams,
  fetchData,
  isAdmins,
}) => {
  const { message } = App.useApp();

  const [optionsPending, setOptionsPending] = useState(true);

  const [unitData, setUnitData] = useState(null);

  // Списки для фильтров
  const [statuses, setStatuses] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [unitsTree, setUnitsTree] = useState([]);
  const [units, setUnits] = useState([]);

  // Окно с фильтрами
  const [isFiltersModalShow, setIsFiltersModalShow] = useState(false);
  const [tempValueFilters, setTempValueFilters] = useState(null);

  const isAnyFilterApplied = useMemo(() => {
    const { filters } = tableParams;

    return Object.entries(filters).some(([key, value]) => {
      const initialValue = initialTableParams.filters[key];
      if (!initialValue && !value) return false;

      if (Array.isArray(value)) {
        return JSON.stringify(value) !== JSON.stringify(initialValue);
      }

      return value !== initialValue;
    });
  }, [tableParams, initialTableParams]);

  // Возвращает плоский список РГ
  const getFlatUnits = useMemo(() => {
    const flat = [];

    function traverse(nodes) {
      nodes.forEach((node) => {
        flat.push(node);
        if (Array.isArray(node.children) && node.children.length > 0) {
          traverse(node.children);
        }
      });
    }

    if (!unitsTree) return [];

    traverse(unitsTree);
    return flat;
  }, [unitsTree]);

  //  initial fetch
  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line
  }, [unit_uuid]);

  const fetchOptions = async () => {
    setOptionsPending(true);
    try {
      const [statusesRes, { units: unitsRes }, unitRes, treeRes] =
        await Promise.all([
          getStatusOptions(),
          getEmployeeUnitsOptions(),
          unit_uuid && getUnit(unit_uuid),
          unit_uuid
            ? getUnitsTree({ unit_uuid, from_unit: true })
            : getUnitsTree({ from_unit: true }),
        ]);

      setStatuses(statusesRes.map((i) => ({ ...i, label: i.name })));
      setUnits(unitsRes);
      setUnitsTree(treeRes);
      setUnitData(unitRes);

      // Инициализация параметров из localStorage
      let localParams =
        JSON.parse(
          localStorage.getItem(getLocalStorageKey(userUuid, unit_uuid)),
        ) || initialTableParams;

      setParams(localParams);

      let users = await fetchUserList();

      // Проверяем необходимость обновления фильтров
      const newFilters = { ...localParams.filters };

      // Если фильтр по рабочей группе, но ее нет в списке, сбросить фильтр
      if (
        newFilters.unit_uuid &&
        !unitsRes.some((u) => u.uuid === newFilters.unit_uuid)
      ) {
        newFilters.unit_uuid = null;
        users = await fetchUserList();
      }

      // Если выбранный автор отсутствует в списке сотрудников, сбросить его
      if (
        localParams.filters.author_uuid &&
        !users.some((e) => e.uuid === localParams.filters.author_uuid)
      ) {
        newFilters.author_uuid = null;
      }

      const effectiveParams = {
        ...localParams,
        filters: {
          ...newFilters,
        },
      };

      const filtersChanged =
        JSON.stringify(newFilters) !== JSON.stringify(localParams.filters);
      if (filtersChanged) {
        setParams({
          ...localParams,
          filters: newFilters,
          pagination: { ...localParams.pagination, current: 1 },
        });
      }

      await fetchData(effectiveParams);
    } catch (error) {
      message.open({ type: "error", content: error.message, duration: 10 });
    } finally {
      setOptionsPending(false);
    }
  };

  // Получение списка пользователей для фильтров
  const fetchUserList = useCallback(
    async (unit_uuid) => {
      try {
        let list = [];
        if (isAdmins && !unit_uuid) {
          const { employees } = await getEmployeesOptions({
            is_dismissed: false,
          });
          list = employees.map(({ employee }) => employee);
        } else {
          const { members } = await getUnitEmployees({
            ...(unit_uuid && {
              unit_uuid: unit_uuid,
            }),
          });
          list = members;
        }

        const res = list.map((item) => {
          const { lastname, firstname, patronymic } = item;
          return {
            ...item,
            label: `${lastname || ""} ${firstname || ""} ${patronymic || ""}`,
          };
        });

        setAuthors(res);
        return res;
      } catch (error) {
        console.log(error);
      }
    },
    [isAdmins],
  );

  const updateFilters = useCallback(
    (localParams, newFilters, resetPage = false) => {
      const updatedFilters = { ...localParams.filters, ...newFilters };

      const updatedTableParams = {
        ...localParams,
        filters: updatedFilters,
        pagination: {
          ...localParams.pagination,
          current: resetPage ? 1 : localParams.pagination.current,
        },
      };

      setParams(updatedTableParams);
      fetchData(updatedTableParams);
    },
    [setParams, fetchData],
  );

  // Обработчик для смены фильтров
  const handleFilterChange = useCallback(
    async (name, val) => {
      const additionalFilters = {};

      if (name === FILTER_NAMES.UNITS_UUIDS) {
        additionalFilters.access_level = "all";
      }

      if (name === FILTER_NAMES.ACCESS_LEVEL) {
        additionalFilters.units_uuids =
          val === "all"
            ? []
            : val === "root"
              ? getFlatUnits.filter(({ readOnly }) => !readOnly)
              : getFlatUnits.filter(({ readOnly }) => readOnly);
      }

      updateFilters(
        tableParams,
        {
          [name]: val || null,
          ...additionalFilters,
        },
        true,
      );
    },
    [tableParams, updateFilters, getFlatUnits],
  );

  // Обработчик для открытия модалки с фильтрами
  const openFiltersModal = () => {
    setTempValueFilters({
      ...tableParams.filters,
    });
    setIsFiltersModalShow(true);
  };

  // Очистка всех фильтров
  const clearFilters = () => {
    const updatedTableParams = {
      ...tableParams,
      filters: { ...initialTableParams.filters },
      pagination: {
        ...tableParams.pagination,
        current: 1, // Сбрасываем на первую страницу
      },
    };

    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  // Применение выбранных фильтров из модалки
  const applyFilters = useCallback(() => {
    updateFilters(tableParams, { ...tempValueFilters }, true);
    setTempValueFilters(null);
    setIsFiltersModalShow(false);
  }, [tableParams, tempValueFilters, updateFilters]);

  // Обработчик для смены временных фильтров в модалке
  const handleTempFilterChange = useCallback(
    (name, val) => {
      setTempValueFilters((prev) => {
        const additionalFilters = {};

        if (name === FILTER_NAMES.UNITS_UUIDS) {
          additionalFilters.access_level = "all";
        }

        if (name === FILTER_NAMES.ACCESS_LEVEL) {
          additionalFilters.units_uuids =
            val === "all"
              ? []
              : val === "root"
                ? getFlatUnits.filter(({ readOnly }) => !readOnly)
                : getFlatUnits.filter(({ readOnly }) => readOnly);
        }

        return {
          ...prev,
          [name]: val || null,
          ...additionalFilters,
        };
      });

    },
    [getFlatUnits],
  );

  return {
    statuses,
    authors,
    unitsTree,
    unitData,
    units,
    tempValueFilters,
    optionsPending,
    isAnyFilterApplied,
    isFiltersModalShow,
    handleFilterChange,
    openFiltersModal,
    clearFilters,
    applyFilters,
    setIsFiltersModalShow,
    handleTempFilterChange,
  };
};
